import React, { useState } from 'react';
import { CreditCard, CheckCircle2, AlertCircle, ChevronDown, HelpCircle, DollarSign, Bitcoin, Coins } from 'lucide-react';
import { Button } from '../../../ui/button';
import type {
  Currency,
  PaymentData,
  PaymentInfoStatus,
} from '../../../../hooks/api/paymentInfo';

export type { Currency, PaymentData, PaymentInfoStatus };

/** Modalidad ya cargada y guardada contra psima (currency + status del GET/POST). */
export interface LoadedPaymentInfo {
  data: PaymentData;
  status: PaymentInfoStatus;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MODALITY_LABELS: Record<Currency, string> = {
  USD: 'USD vía ACH',
  ARS: 'Pesos argentinos',
  USDT: 'USDT',
};

function maskAccount(value: string): string {
  if (value.length <= 4) return value;
  return `****${value.slice(-4)}`;
}

function maskWallet(value: string): string {
  if (value.length <= 10) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function formatCuitDisplay(digits: string): string {
  if (digits.length !== 11) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusChip: React.FC<{ status: PaymentInfoStatus }> = ({ status }) =>
  status === 'complete' ? (
    <span
      data-testid="datos-cobro-chip-completo"
      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200"
    >
      <CheckCircle2 size={11} />
      Completo
    </span>
  ) : (
    <span
      data-testid="datos-cobro-chip-incompleto"
      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-200"
    >
      <AlertCircle size={11} />
      Incompleto
    </span>
  );

const ModalityIcon: React.FC<{ currency: Currency }> = ({ currency }) => {
  if (currency === 'USD') return <DollarSign size={16} className="text-violet-500" />;
  if (currency === 'USDT') return <Bitcoin size={16} className="text-violet-500" />;
  return <Coins size={16} className="text-violet-500" />;
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}

function PaymentSummary({ info, onModify }: { info: LoadedPaymentInfo; onModify: () => void }) {
  const { data, status } = info;
  return (
    <div
      data-testid="datos-cobro-card"
      className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5 flex flex-col gap-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-violet-100 rounded-lg p-1.5">
            <ModalityIcon currency={data.currency} />
          </div>
          <span className="font-semibold text-gray-800 text-sm">
            {MODALITY_LABELS[data.currency]}
          </span>
        </div>
        <StatusChip status={status} />
      </div>

      {status === 'incomplete' && (
        <p
          data-testid="datos-cobro-incompleto-copy"
          className="text-xs text-orange-600 -mt-2"
        >
          Faltan datos por completar — tu liquidación podría demorarse hasta que los completes.
        </p>
      )}

      {/* Datos */}
      <div className="flex flex-col gap-2">
        {data.currency === 'USD' && (
          <>
            <SummaryRow label="Plataforma" value={data.plataforma} />
            <SummaryRow label="Beneficiario" value={data.nombreBeneficiario} />
            <SummaryRow label="Banco" value={data.nombreBanco} />
            <SummaryRow label="Cuenta" value={maskAccount(data.numeroCuenta)} />
            <SummaryRow
              label="Tipo de cuenta"
              value={data.tipoCuenta === 'checking' ? 'Checking' : 'Savings'}
            />
          </>
        )}

        {data.currency === 'ARS' && (
          <>
            <SummaryRow label="LB Tag" value={`$${data.lbTag}`} />
            <SummaryRow label="CUIT" value={formatCuitDisplay(data.cuit)} />
          </>
        )}

        {data.currency === 'USDT' && (
          <>
            <SummaryRow label="Red" value="Polygon (MATIC)" />
            {data.plataformaOWallet && (
              <SummaryRow label="Plataforma / Wallet" value={data.plataformaOWallet} />
            )}
            <SummaryRow label="Wallet" value={maskWallet(data.direccionWallet)} />
          </>
        )}
      </div>

      {/* Acción */}
      <div className="flex justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onModify} data-testid="modificar-datos-btn">
          Modificar datos
        </Button>
      </div>
    </div>
  );
}

function EmptyPaymentState({ onLoad }: { onLoad: () => void }) {
  return (
    <div
      data-testid="datos-cobro-empty"
      className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col items-center gap-3 text-center"
    >
      <div className="bg-violet-100 rounded-2xl p-3">
        <CreditCard size={24} className="text-violet-500" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-gray-800">
          Todavía no cargaste tus datos de cobro
        </p>
        <p className="text-xs text-gray-500 max-w-xs">
          Las sesiones se liquidan a mes vencido. Los pagos se procesan hasta el día 5 del mes siguiente. Cargá tus datos para asegurarte de recibirlos a tiempo.
        </p>
      </div>
      <Button variant="default" size="sm" onClick={onLoad} data-testid="datos-cobro-cta-btn">
        Configurar modalidad de cobro
      </Button>
    </div>
  );
}

/**
 * `exists:true` pero `currency:null` (y por lo tanto `data:null`): la psicóloga
 * tiene un medio de pago cargado que esta integración no administra (ej. EUR,
 * o una fila cargada a mano por Finanzas antes de este endpoint). Sin CTA de
 * edición a propósito — habilitarla dejaría que la psicóloga pise un dato que
 * hoy gestiona Finanzas directamente, sin que este formulario sepa mapearlo.
 */
function FinanzasManagedState() {
  return (
    <div
      data-testid="datos-cobro-gestionado-finanzas"
      className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col items-center gap-3 text-center"
    >
      <div className="bg-violet-100 rounded-2xl p-3">
        <CreditCard size={24} className="text-violet-500" />
      </div>
      <p className="text-sm font-semibold text-gray-800">
        Tus datos de cobro los gestiona Finanzas directamente
      </p>
      <p className="text-xs text-gray-500 max-w-xs">
        Si necesitás modificarlos, escribí a <span className="font-medium text-violet-700">liquidaciones@psimammoliti.com</span>.
      </p>
    </div>
  );
}

// ─── Info & FAQ Accordion ─────────────────────────────────────────────────────

const INFO_POINTS = [
  'Las sesiones se liquidan a mes vencido. Los pagos se procesan hasta el día 5 del mes siguiente. Si el día 5 coincide con un fin de semana o feriado, el pago se realiza el primer día hábil posterior.',
  'El detalle de cada liquidación está disponible en esta sección antes del procesamiento del pago.',
  'Cada profesional es responsable de cumplir con sus obligaciones fiscales, de acuerdo con la normativa vigente en su país.',
  'Psi Mammoliti garantiza el pago del monto bruto comprometido. Cualquier comisión, costo de recepción o cargo aplicado por la entidad o plataforma receptora es responsabilidad de la profesional.',
];

interface FaqItemData {
  q: string;
  // a puede ser string (texto simple) o React.ReactNode (contenido enriquecido)
  a: string | React.ReactNode;
}

const FAQ_ITEMS: FaqItemData[] = [
  {
    q: '¿Cuándo se realizan los pagos?',
    a: 'Las sesiones de cada mes se liquidan hasta el día 5 del mes siguiente. Si el 5 cae sábado, domingo o feriado, el pago se procesa el primer día hábil posterior. Ejemplo: sesiones de julio → pago hasta el 5 de agosto. Para cobros en USD vía ACH, la acreditación puede demorar entre 24 y 72 horas hábiles adicionales desde el momento del procesamiento.',
  },
  {
    q: '¿Cuáles son las modalidades de cobro disponibles?',
    a: (
      <div className="flex flex-col gap-4 pb-3">
        <p className="text-sm text-gray-500 leading-relaxed">
          Queremos que cada profesional pueda elegir la modalidad que mejor se adapte a su país, sus preferencias y la forma en que habitualmente gestiona sus fondos.
        </p>
        <p className="text-sm text-gray-500 leading-snug">Actualmente contamos con tres opciones:</p>

        {/* USD vía ACH */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-violet-400 shrink-0" />
            <span className="text-sm font-semibold text-gray-700">USD vía ACH</span>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            Recibís el pago en dólares mediante una transferencia bancaria local de Estados Unidos. Esta modalidad funciona con plataformas como ARQ (ex DolarApp), Wise, Payoneer, Revolut, Takenos o Global66, entre otras.
          </p>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            Al crear tu usuario en estas plataformas, se genera una cuenta en EE.UU. compatible con transferencias ACH, sin que tengas que realizar gestiones bancarias adicionales.
          </p>
        </div>

        {/* USDT */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-violet-400 shrink-0" />
            <span className="text-sm font-semibold text-gray-700">USDT vía Polygon</span>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            Recibís el pago en USDT, una moneda digital cuyo valor se mantiene cercano al dólar estadounidense.
          </p>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            El monto en dólares se convierte a USDT utilizando el tipo de cambio de LB Finanzas vigente al día efectivo del pago. Esta modalidad puede utilizarse con billeteras o exchanges compatibles con USDT por red Polygon, como Binance, Bitso, Lemon u otras plataformas similares.
          </p>
        </div>

        {/* AR$ */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-violet-400 shrink-0" />
            <span className="text-sm font-semibold text-gray-700">AR$ vía LB Finanzas</span>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            Modalidad disponible para residentes en Argentina.
          </p>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            Recibís el equivalente en pesos argentinos en tu cuenta de LB Finanzas. El monto en dólares se convierte a AR$ utilizando el tipo de cambio de LB Finanzas vigente al día efectivo del pago.
          </p>
          <p className="text-sm text-gray-500 leading-relaxed pl-4">
            Para utilizar esta modalidad necesitás contar con LB Tag y CUIT.
          </p>
        </div>

        <p className="text-sm text-gray-400 leading-snug italic">
          Podés modificar tu modalidad de cobro más adelante si lo necesitás.
        </p>
      </div>
    ),
  },
  {
    q: '¿Puedo cambiar de modalidad más adelante?',
    a: 'Sí. Los cambios realizados antes del último día del mes aplican en esa liquidación. Los realizados después aplican en la siguiente.',
  },
  {
    q: '¿Qué pasa si no cargo mis datos antes del cierre del mes?',
    a: 'El pago no se pierde. Una vez cargados los datos, se gestiona en un proceso especial de pago fuera del ciclo habitual del día 5, en el transcurso del mismo mes.',
  },
  {
    q: '¿Qué pasa si actualizo mis datos o cambio de plataforma?',
    a: 'Podés actualizarlos en cualquier momento. Los cambios antes del último día del mes aplican en esa liquidación. Los realizados después aplican en la siguiente.',
  },
  {
    q: '¿Qué hago si no recibo el pago o noto errores en mi liquidación?',
    a: (
      <div className="flex flex-col gap-3 pb-3">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-gray-700 leading-snug">Si no recibís el pago</p>
          <p className="text-sm text-gray-500 leading-relaxed">Verificá que tus datos estén correctamente cargados y que haya transcurrido el período de procesamiento completo. Para cobros en USD vía ACH, esperá al menos 72 horas hábiles desde el día 5. Si el pago no aparece, enviá tu consulta a <span className="font-medium text-violet-700">liquidaciones@psimammoliti.com</span> con tu nombre completo, el mes al que corresponde, la modalidad configurada y la plataforma.</p>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-gray-700 leading-snug">Si notás errores en tu liquidación</p>
          <p className="text-sm text-gray-500 leading-relaxed">Si el monto no coincide con lo esperado o hay sesiones que no aparecen, escribí a <span className="font-medium text-violet-700">liquidaciones@psimammoliti.com</span> con tu nombre completo, el período afectado y el detalle del error. Cuanta más información incluyas, más rápida será la resolución.</p>
        </div>
      </div>
    ),
  },
  {
    q: '¿Tengo que emitir factura para cobrar?',
    a: 'No es un requisito. Si querés facturar, podés hacerlo mediante Factura E de exportación de servicios o su equivalente en tu país. Para obtener los datos de la empresa escribí a liquidaciones@psimammoliti.com.',
  },
  {
    q: '¿Qué plataformas son compatibles para cobrar en USD?',
    a: (
      <div className="flex flex-col gap-2 text-sm text-gray-500 leading-relaxed">
        <p>Los pagos en dólares se realizan exclusivamente mediante transferencias ACH, un sistema de transferencias bancarias locales de Estados Unidos.</p>
        <p>Podés utilizar cualquier plataforma que te proporcione una cuenta bancaria en EE.UU. compatible con transferencias ACH.</p>
        <p>Las plataformas que actualmente tenemos confirmadas y que utilizan distintas profesionales del equipo son:</p>
        <ul className="pl-4 flex flex-col gap-0.5">
          {['ARQ', 'Wise', 'Payoneer', 'Revolut', 'Takenos', 'Global66'].map(p => (
            <li key={p} className="flex items-center gap-1.5"><span className="text-violet-400">•</span>{p}</li>
          ))}
        </ul>
        <p>Si utilizás otra plataforma, también puede ser compatible. Antes de configurarla, verificá que efectivamente brinde una cuenta en Estados Unidos para recibir transferencias ACH.</p>
        <p>Las cuentas bancarias locales (aunque sean en dólares) generalmente no son compatibles con este tipo de transferencias.</p>
      </div>
    ),
  },
];

function FaqItem({ q, a, index }: { q: string; a: string | React.ReactNode; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-gray-100 last:border-b-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3 text-left gap-3"
        data-testid={`faq-toggle-${index}`}
        aria-expanded={open}
      >
        <span className="text-sm text-gray-700 font-medium leading-snug">{q}</span>
        <ChevronDown
          size={15}
          className={`text-gray-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          data-testid={`faq-answer-${index}`}
          className="mb-3 ml-1 pl-3 border-l-2 border-violet-200 bg-violet-50/40 rounded-r-md py-2 pr-2"
        >
          {typeof a === 'string' ? (
            <p className="text-sm text-gray-500 leading-relaxed">{a}</p>
          ) : a}
        </div>
      )}
    </div>
  );
}

function InfoImportanteAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="profile-card !top-0 !p-0 !mb-0 overflow-hidden"
      data-testid="info-importante-accordion"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left transition-colors hover:bg-gray-50"
        data-testid="info-importante-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <AlertCircle size={17} className="text-[#6D57BC]" />
          <span className="text-sm font-medium text-gray-700">Información importante</span>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`border-t border-gray-100 px-6 py-2 ${open ? '' : 'hidden'}`}>
        <ul className="flex flex-col gap-2 my-3 pl-3 border-l-2 border-violet-200 bg-violet-50/40 rounded-r-md py-3 pr-2" data-testid="info-relevante-block">
          {INFO_POINTS.map((point, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-500 leading-snug">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
              {point}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FaqAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="profile-card !top-0 !p-0 !mb-0 overflow-hidden"
      data-testid="faq-accordion"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left transition-colors hover:bg-gray-50"
        data-testid="faq-accordion-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <HelpCircle size={17} className="text-[#6D57BC]" />
          <span className="text-sm font-medium text-gray-700">Preguntas frecuentes</span>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`border-t border-gray-100 px-6 py-2 ${open ? '' : 'hidden'}`} data-testid="faq-block">
        {FAQ_ITEMS.map((item, i) => (
          <FaqItem key={i} index={i} q={item.q} a={item.a} />
        ))}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

interface DatosDeCobroSectionProps {
  paymentInfo: LoadedPaymentInfo | null;
  /** `exists:true` + `currency:null` — medio de pago que esta integración no
   *  administra (gestionado por Finanzas). Distinto de "vacío": no hay CTA
   *  de edición. Ver FinanzasManagedState. */
  managedByFinanzas?: boolean;
  onLoad?: () => void;
  onModify?: () => void;
}

export const DatosDeCobroSection: React.FC<DatosDeCobroSectionProps> = ({
  paymentInfo,
  managedByFinanzas = false,
  onLoad = noop,
  onModify = noop,
}) => {
  return (
    <div className="flex flex-col gap-4" data-testid="datos-cobro-section">
      {/* Divisor */}
      <div className="flex items-center gap-3 pt-2">
        <div className="flex-1 h-px bg-gray-200" />
        <span
          data-testid="datos-cobro-titulo"
          className="text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap"
        >
          Datos de Cobro
        </span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Subtítulo — solo en estado vacío (no aplica si Finanzas ya lo gestiona) */}
      {!paymentInfo && !managedByFinanzas && (
        <p
          data-testid="datos-cobro-subtitulo"
          className="text-xs text-gray-400 text-center leading-snug -mt-2"
        >
          Elegí cómo querés recibir tus liquidaciones. Podrás modificar esta configuración cuando lo necesites.
        </p>
      )}

      {managedByFinanzas ? (
        <FinanzasManagedState />
      ) : paymentInfo ? (
        <PaymentSummary info={paymentInfo} onModify={onModify} />
      ) : (
        <EmptyPaymentState onLoad={onLoad} />
      )}

      <div className="flex flex-col gap-2">
        <InfoImportanteAccordion />
        <FaqAccordion />
      </div>
    </div>
  );
};
